// ==================================
// Pagina Lista de Administradores
// ==================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Header } from '@/componentes/layout';
import {
  Button,
  Input,
  Select,
  Table,
  Badge,
  Card,
  Pagination,
  ConfirmDialog,
} from '@/componentes/ui';
import { superadminsServicio } from '@/servicios';
import { useAuthStore } from '@/estado/authStore';
import type { SuperAdmin } from '@/tipos';

const estadoOptions = [
  { value: '', label: 'Todos' },
  { value: 'activo', label: 'Activos' },
  { value: 'inactivo', label: 'Inactivos' },
  { value: 'suspendido', label: 'Suspendidos' },
];

function getEstadoBadgeVariant(
  estado: string
): 'success' | 'warning' | 'danger' | 'info' | 'gray' {
  const variants: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'gray'> = {
    activo: 'success',
    inactivo: 'gray',
    suspendido: 'danger',
  };
  return variants[estado] || 'gray';
}

export function Administradores() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const usuario = useAuthStore((state) => state.usuario);
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('');
  const [pagina, setPagina] = useState(1);

  // Estado para el dialogo de confirmacion de eliminacion
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    admin: SuperAdmin | null;
  }>({ isOpen: false, admin: null });

  const { data, isLoading } = useQuery({
    queryKey: ['superadmins', busqueda, estado, pagina],
    queryFn: () =>
      superadminsServicio.listar({
        busqueda: busqueda || undefined,
        estado: estado || undefined,
        pagina,
        limite: 10,
      }),
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => superadminsServicio.eliminar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmins'] });
      toast.success('Administrador eliminado');
      setDeleteDialog({ isOpen: false, admin: null });
    },
    onError: () => {
      toast.error('Error al eliminar el administrador');
    },
  });

  const handleDeleteClick = (admin: SuperAdmin, e: React.MouseEvent) => {
    e.stopPropagation();
    if (admin.id === usuario?.id) {
      toast.error('No puedes eliminarte a ti mismo');
      return;
    }
    setDeleteDialog({ isOpen: true, admin });
  };

  const columns = [
    {
      key: 'nombre',
      header: 'Nombre',
      render: (admin: SuperAdmin) => (
        <div>
          <p className="font-medium text-obsidian-900">
            {admin.nombre} {admin.apellidoPaterno} {admin.apellidoMaterno || ''}
          </p>
          <p className="text-sm text-stone-500">{admin.email}</p>
        </div>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (admin: SuperAdmin) => (
        <Badge variant={getEstadoBadgeVariant(admin.estado)}>
          {admin.estado}
        </Badge>
      ),
    },
    {
      key: 'ultimoAcceso',
      header: 'Último Acceso',
      render: (admin: SuperAdmin) => (
        <span className="text-stone-500">
          {admin.ultimoAcceso
            ? new Date(admin.ultimoAcceso).toLocaleDateString('es-MX')
            : 'Nunca'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Registro',
      render: (admin: SuperAdmin) => (
        <span className="text-stone-500">
          {new Date(admin.createdAt).toLocaleDateString('es-MX')}
        </span>
      ),
    },
    {
      key: 'acciones',
      header: 'Acciones',
      render: (admin: SuperAdmin) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/administradores/${admin.id}`);
            }}
            className="p-1.5 text-stone-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Ver detalle"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={(e) => handleDeleteClick(admin, e)}
            className="p-1.5 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Eliminar"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Header titulo="Administradores" />

      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Filtros */}
        <Card>
          <div className="space-y-4">
            {/* Fila 1: Busqueda y boton */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={busqueda}
                  onChange={(e) => {
                    setBusqueda(e.target.value);
                    setPagina(1);
                  }}
                  icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>}
                />
              </div>
              <Button
                variant="primary"
                onClick={() => navigate('/administradores/nuevo')}
                className="w-full sm:w-auto whitespace-nowrap"
                leftIcon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
              >
                Nuevo Administrador
              </Button>
            </div>

            {/* Fila 2: Filtros */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Select
                options={estadoOptions}
                value={estado}
                onChange={(e) => {
                  setEstado(e.target.value);
                  setPagina(1);
                }}
              />
            </div>
          </div>
        </Card>

        {/* Tabla */}
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            data={data?.data || []}
            keyExtractor={(admin) => admin.id}
            loading={isLoading}
            emptyMessage="No se encontraron administradores"
            onRowClick={(admin) => navigate(`/administradores/${admin.id}`)}
          />
        </div>

        {/* Paginacion */}
        {data?.paginacion && (
          <Pagination
            pagina={data.paginacion.pagina}
            totalPaginas={data.paginacion.totalPaginas}
            onCambiarPagina={setPagina}
          />
        )}
      </div>

      {/* Dialogo de confirmacion para eliminar */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, admin: null })}
        onConfirm={() => {
          if (deleteDialog.admin) {
            eliminarMutation.mutate(deleteDialog.admin.id);
          }
        }}
        title="Eliminar administrador"
        message={
          deleteDialog.admin ? (
            <>
              <p>
                ¿Estás seguro de que deseas eliminar al administrador{' '}
                <strong>{deleteDialog.admin.nombre} {deleteDialog.admin.apellidoPaterno}</strong>?
              </p>
              <p className="mt-2 text-red-600 font-medium">
                Esta acción no se puede deshacer.
              </p>
            </>
          ) : null
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        loading={eliminarMutation.isPending}
      />
    </div>
  );
}

export default Administradores;
